import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { InlineAlert } from '@scramble/ui';
import { useGetPaymentMethods } from '@/query/queries/cash/useGetPaymentMethods';
import { useGetPaymentCalculatedAmount } from '@/query/queries/cash/useGetPaymentCalculatedAmount';
import { PaymentMethodSelectModal } from '@/modals/PaymentMethodSelectModal';
import { prettyMoney } from '@scramble/utils';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useModal } from '@/providers/modal';
import { calculateFee, getProcessingTime } from '../../utils';
import IconChevron from '@scramble/design-system/icons/chevron-right.svg';
import s from './index.module.scss';

export const PaymentMethod = ({ amount, method, className, onChange }) => {
  const { openSingleModal } = useModal();
  const { t } = useTranslation();
  const { data, isLoading } = useGetPaymentMethods();
  const { data: calculation } = useGetPaymentCalculatedAmount({
    amount,
    payment_method: method?.type,
  });

  useEffect(() => {
    if (!method && data?.length > 0) {
      onChange(data[0]);
    }
  }, [data, method, onChange]);

  const onClick = () => {
    openSingleModal(MODAL_IDS.payment_method_select_modal);
  };

  const feeText = useMemo(() => {
    if (!calculation) return '';
    const totalFee = calculation.amount;
    const fee = calculateFee(totalFee, amount);
    if (fee <= 0) return t('funds:payment_method.no_fee');
    if (fee > 0) return prettyMoney(fee);
    return '';
  }, [amount, calculation]);

  if (!data?.length && !isLoading)
    return (
      <div className={s.topup_empty}>
        <InlineAlert
          type="warning"
          title={t('funds:payment_method.no_payment_methods')}
        />
      </div>
    );

  if (!method) return null;

  return (
    <div className={classnames(s.wrapper, className)}>
      <div className="f-body-sm f-500">{t('funds:payment_method.title')}</div>
      <div className={s.select} onClick={onClick}>
        <div className={s.header}>
          <div className={s.payment}>
            <img src={method.icon} alt="icon" width={20} height={20} />
            <span className="f-body-md f-500">{method.title}</span>
          </div>
          <div className={s.icon}>
            <img src={IconChevron} alt="icon" width={20} height={20} />
          </div>
        </div>
        <div className={s.summary}>
          <div className={s.row}>
            <span className="f-body-md">
              {t('funds:payment_method.processing_time')}
            </span>
            <span className="f-body-md f-500">
              {getProcessingTime(method.type)}
            </span>
          </div>
          <div className={s.row}>
            <span className="f-body-md">{t('funds:payment_method.fee')}</span>
            <span className="f-body-md f-500">{feeText}</span>
          </div>
        </div>
      </div>

      <PaymentMethodSelectModal amount={amount} selected={method} onChange={onChange} />
    </div>
  );
};
